import { DashboardFavorite } from 'lib-dashboard-types';
import { useCallback, useMemo } from 'react';
import { DeepWritable } from 'ts-essentials';

import { UserStorageDashboard } from 'apis/userStorage';
import { useServerStorage } from 'utils/useServerStorage';

type UseDashboardLandingPageResult = {
  isLandingPage: (favorite: DashboardFavorite) => boolean;
  setLandingPage: (
    arg: NonNullable<
      NonNullable<UserStorageDashboard['dashboard']>['landingPage']
    >,
  ) => void;
  resetLandingPage: () => void;
};

export const useDashboardLandingPage = (): UseDashboardLandingPageResult => {
  const storage = useServerStorage('user');
  const { landingPage } = storage.data.dashboard ?? {};

  /** Returns whether the given favorite is set as the user's landing page. */
  const isLandingPage = useCallback(
    (favorite: DashboardFavorite) => {
      const { type, url } = landingPage ?? {};

      return (
        type === 'user_preferred' &&
        url?.path === '/dashboard/queues' &&
        url.params.search.type === 'favourite' &&
        url.params.search.id === favorite.id
      );
    },
    [landingPage],
  );

  /**
   * Sets the landing page the user will be redirected to automatically when
   * they load the dashboard the first time.
   */
  const setLandingPage: UseDashboardLandingPageResult['setLandingPage'] =
    useCallback(
      ({ type, url }) => {
        // If the landing page type is 'user_preferred', we skip persisting the last visited URL, so
        // the user always returns to the landing page URL they have specifically chosen.
        if (type === 'last_visited' && landingPage?.type === 'user_preferred') {
          return;
        }

        storage.set({
          dashboard: (draft) => {
            draft.landingPage = {
              type,
              url: url as DeepWritable<typeof url>,
            };

            // Clean up some deprecated properties.
            // @ts-expect-error Deprecated property.
            delete draft.defaultDashboardType;
            // @ts-expect-error Deprecated property.
            delete draft.defaultQueueType;
          },
        });
      },
      [landingPage?.type, storage],
    );

  /**
   * Resets the landing page, so the user is taken to the default dashboard on
   * their next visit based on their access.
   */
  const resetLandingPage = useCallback(() => {
    storage.set({
      dashboard: (draft) => {
        delete draft.landingPage;
      },
    });
  }, [storage]);

  return useMemo(
    () => ({ isLandingPage, setLandingPage, resetLandingPage }),
    [isLandingPage, setLandingPage, resetLandingPage],
  );
};
